/**
 * Pricing Defaults — per-brand product RRP + tier prices.
 *
 * Single source of truth for prices. Every product belongs to exactly one
 * brand (resolved from its category). Each product carries four prices:
 *   - rrp             (customer-facing default — used on quotes)
 *   - trade           (installer cost at Trade tier)
 *   - approved        (installer cost at Approved tier)
 *   - elite_approved  (installer cost at Elite Approved tier)
 *
 * Installers only ever see RRP + their own tier price on /installer/... —
 * never the prices of tiers they aren't on.
 */
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, PoundSterling, Save, Loader2, Link2, ListPlus, Download, Trash2, Pin, PinOff, UploadCloud } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import useHomePath from '@/hooks/useHomePath';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';
import { ProEpoxyPackEditor } from '@/components/proepoxy/ProEpoxyPackEditor';
import { QuoteToolPriceCheck } from '@/components/pricing/QuoteToolPriceCheck';
import LinkToXeroButton from '@/components/pricing/LinkToXeroButton';
import { XeroLinkStatusCard } from '@/components/pricing/XeroLinkStatusCard';
import { CostCell } from '@/components/pricing/CostCell';
import { XeroFollowCard } from '@/components/pricing/XeroFollowCard';
import { ProEpoxyItemLinks } from '@/components/pricing/ProEpoxyItemLinks';
import { MarginCell, UsedInTags } from '@/components/pricing/MarginCell';
import { prodecorMatchNames } from '@/data/prodecorProducts';
import CoverageSettingsCard from '@/pages/staff/CoverageSettingsCard';
import { TierManagerCard } from '@/components/pricing/TierManagerCard';
import { ProductNameEdit } from '@/components/pricing/ProductNameEdit';
import useInstallerTiers from '@/hooks/useInstallerTiers';

const BRAND_TABS = [
  { key: 'probound', label: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor', color: '#D5AB09', ink: '#0B0B0E' },
];

const RRP_COLUMN = { key: 'rrp', label: 'RRP', help: 'Customer-facing price · used on installer quotes', strong: true };

export default function PricingTiers() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { isStaff } = useContext(AuthContext);
  const { tiers, setTiers, reload: reloadTiers } = useInstallerTiers();
  const TIER_COLUMNS = useMemo(() => [RRP_COLUMN, ...tiers.map((t) => ({
    key: t.slug, label: t.label, discount: t.discount_pct,
    help: t.discount_pct != null ? `Installer cost — ${t.label} tier · worked out as RRP − ${t.discount_pct}%` : `Installer cost — ${t.label} tier`,
  }))], [tiers]);
  const [activeBrand, setActiveBrand] = useState('probound');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});            // { product_id: { rrp, trade, approved, elite_approved } }
  const [highlight, setHighlight] = useState({ missing: new Set(), xero: new Set() }); // quote-tool price check
  const [checkKey, setCheckKey] = useState(0);
  const [savingIds, setSavingIds] = useState(new Set());
  const [autoMatching, setAutoMatching] = useState(false);
  const [applyingPricelist, setApplyingPricelist] = useState(false);
  const [usedIn, setUsedIn] = useState({});
  const [usedLoaded, setUsedLoaded] = useState(false);
  const [rowFilter, setRowFilter] = useState('all'); // all | unused | nocost | lowmargin
  const onLinkStatus = useCallback((d) => { setUsedIn(Object.fromEntries((d?.products || []).map(p => [p.id, p.used_in]))); setUsedLoaded(true); }, []);
  useEffect(() => {
    if (!isStaff) return;
    fetch(`${API_URL}/api/staff/product-pricing/quote-tool-names`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ brand: 'prodecor', names: prodecorMatchNames() }) })
      .then(() => setCheckKey(k => k + 1)).catch(() => {});
  }, [isStaff]);
  const hasPricelist = activeBrand === 'probound' || activeBrand === 'prodecor';

  /**
   * Bulk-match every unlinked product's SKU against the mirrored Xero
   * catalogue. Backed by POST /api/xero/auto-match — links exact-SKU
   * matches and returns a summary the toast surfaces.
   */
  const [usingXero, setUsingXero] = useState(false);
  const xeroCandidates = useMemo(() => products.filter(p => p.xero_price != null && Number(p.xero_price) > 0), [products]);
  const xeroBlankTrade = useMemo(() => xeroCandidates.filter(p => !(Number(p.prices?.trade) > 0)), [xeroCandidates]);

  // Per row: overwrite this product's Trade cell with its synced Xero sales price.
  const copyXeroPriceRow = async (product) => {
    setSavingIds(s => new Set(s).add(product.id));
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/${product.id}/use-xero-price`, { method: 'POST', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Failed');
      setProducts(ps => ps.map(p => p.id === product.id ? body : p));
      setEdits(prev => { const n = { ...prev }; delete n[product.id]; return n; });
      setCheckKey(k => k + 1);
      toast.success(`${product.name}: Trade set to £${Number(body.prices.trade).toFixed(2)} from Xero`);
    } catch (e) { toast.error(e.message); } finally {
      setSavingIds(s => { const n = new Set(s); n.delete(product.id); return n; });
    }
  };

  // Bulk: fill every EMPTY Trade cell for this brand from Xero (never overwrites typed prices).
  const copyXeroPricesBulk = async () => {
    setUsingXero(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/use-xero-prices?brand=${activeBrand}`, { method: 'POST', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Failed');
      toast.success(`${body.filled} Trade price${body.filled === 1 ? '' : 's'} filled from Xero${body.skipped ? ` · ${body.skipped} already priced (left as typed)` : ''}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setUsingXero(false); }
  };

  const autoMatchXero = useCallback(async () => {
    setAutoMatching(true);
    try {
      const res = await fetch(`${API_URL}/api/xero/auto-match`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Auto-match failed');
      const parts = [
        `${body.matched} matched`,
        body.skipped_already_linked ? `${body.skipped_already_linked} already linked` : null,
        body.unmatched ? `${body.unmatched} unmatched` : null,
        body.no_sku ? `${body.no_sku} without SKU` : null,
      ].filter(Boolean);
      toast.success(parts.join(' · '));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAutoMatching(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setEdits({});
    try {
      // Role-aware fetch:
      //  – Staff (admin/manager) → /staff/product-pricing → full 4-tier data (editable).
      //  – Installers            → /installer/product-pricing/:brand → RRP + their own
      //                            tier only, other tiers redacted server-side.
      // The installer endpoint returns { id, name, category, rrp, your_price, tier },
      // which we adapt into the same {prices: {rrp, tier}} shape the table
      // consumes so the render tree doesn't have to fork.
      const url = isStaff
        ? `${API_URL}/api/staff/product-pricing?brand=${activeBrand}`
        : `${API_URL}/api/installer/product-pricing/${activeBrand}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load products');
      const data = await res.json();
      const rows = isStaff
        ? data
        : data.map((r) => ({
          ...r,
          prices: {
            rrp: r.rrp || 0,
            [r.tier || 'trade']: r.your_price || 0,
          },
        }));
      setProducts(rows);
      setCheckKey(k => k + 1);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeBrand, isStaff]);

  useEffect(() => {
    // Both installer and staff can view; only staff can edit — enforced by
    // the render tree below (readOnly = !isStaff).
    load();
  }, [load]);

  /**
   * One-tap "Apply printed pricelist": provisions any missing catalogue
   * products for the brand and fills empty Trade prices from the printed
   * Ox pricelist. Idempotent — hand-set Trade prices are never overwritten.
   */
  const applyPricelist = useCallback(async () => {
    setApplyingPricelist(true);
    try {
      const res = await fetch(`${API_URL}/api/material-cost-tiers/${activeBrand}/backfill-catalogue`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Backfill failed');
      const created = body.provisioned?.created_count || 0;
      const filled = body.trade_tier_seed?.stock_products_filled || 0;
      toast.success(
        created || filled
          ? `${filled} Trade price${filled === 1 ? '' : 's'} filled · ${created} product${created === 1 ? '' : 's'} added`
          : 'Already up to date — every pricelist item has a Trade price.'
      );
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplyingPricelist(false);
    }
  }, [activeBrand, load]);

  // Group by category for a tidy editing UI.
  const isUnused = useCallback((p) => usedLoaded && !(usedIn[p.id]?.length) && !(Number(p.qty_on_hand) > 0), [usedIn, usedLoaded]);
  const rowMatches = useCallback((p) => {
    if (rowFilter === 'unused') return isUnused(p);
    if (rowFilter === 'nocost') return p.unit_cost == null;
    if (rowFilter === 'lowmargin') { const t = Number(p.prices?.trade || 0); return p.unit_cost != null && t > 0 && ((t - p.unit_cost) / t) * 100 < 20; }
    return true;
  }, [rowFilter, isUnused]);
  const filterCounts = useMemo(() => ({
    unused: products.filter(isUnused).length,
    nocost: products.filter(p => p.unit_cost == null).length,
    lowmargin: products.filter(p => { const t = Number(p.prices?.trade || 0); return p.unit_cost != null && t > 0 && ((t - p.unit_cost) / t) * 100 < 20; }).length,
  }), [products, isUnused]);

  const byCategory = useMemo(() => {
    const groups = new Map();
    products.filter(rowMatches).forEach(p => {
      if (!groups.has(p.category)) groups.set(p.category, []);
      groups.get(p.category).push(p);
    });
    return Array.from(groups.entries()).map(([cat, items]) => ({ category: cat, items }));
  }, [products, rowMatches]);

  const deleteProduct = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the catalogue? It isn't used by any quote tool or order and has no stock. Past movements stay in the audit log.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/products/${p.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast.success(`Removed ${p.name}`);
      setProducts(ps => ps.filter(x => x.id !== p.id));
    } catch (e) { toast.error(e.message); }
  };

  const onCellChange = (productId, tierKey, value) => {
    setEdits(prev => {
      const row = { ...(prev[productId] || products.find(p => p.id === productId)?.prices || {}) };
      row[tierKey] = value === '' ? '' : Number(value);
      return { ...prev, [productId]: row };
    });
  };

  const pushXero = async (p) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/${p.id}/push-xero`, { method: 'POST', credentials: 'include' });
      const d = await res.json(); if (!res.ok) throw new Error(formatApiError(d) || 'Push failed');
      setProducts(ps => ps.map(x => x.id === d.product.id ? d.product : x));
      toast.success(`${p.name} updated in Xero${d.sales_price != null ? ` · sales price £${Number(d.sales_price).toFixed(2)}` : ''}`);
    } catch (e) { toast.error(e.message); }
  };
  const takeXero = async (p) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/${p.id}/take-xero`, { method: 'POST', credentials: 'include' });
      const row = await res.json(); if (!res.ok) throw new Error(formatApiError(row) || 'Could not update');
      setProducts(ps => ps.map(x => x.id === row.id ? row : x));
      toast.success(`${p.name}: Trade set to Xero's £${Number(row.prices.trade).toFixed(2)}`);
    } catch (e) { toast.error(e.message); }
  };
  const togglePin = async (p, pinned) => {
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/${p.id}/pin`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pinned }) });
      const row = await res.json();
      if (!res.ok) throw new Error(formatApiError(row) || 'Could not update');
      setProducts(ps => ps.map(x => x.id === row.id ? row : x));
      toast.success(pinned ? `${p.name} pinned — auto-follow will leave it alone` : `${p.name} now follows Xero again`);
    } catch (e) { toast.error(e.message); }
  };
  const saveRow = async (product) => {
    const row = edits[product.id];
    if (!row) return;
    // Coerce empties → 0 so the backend Pydantic model accepts the payload.
    const payload = Object.fromEntries(TIER_COLUMNS.map(c => [c.key, Number(row[c.key] ?? product.prices[c.key] ?? 0) || 0]));
    setSavingIds(s => new Set(s).add(product.id));
    try {
      const res = await fetch(`${API_URL}/api/staff/product-pricing/${product.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Save failed');
      const updated = await res.json();
      setProducts(ps => ps.map(p => p.id === product.id ? updated : p));
      setEdits(prev => { const n = { ...prev }; delete n[product.id]; return n; });
      setCheckKey(k => k + 1);
      toast.success('Saved.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingIds(s => { const n = new Set(s); n.delete(product.id); return n; });
    }
  };

  const dirtyCount = Object.keys(edits).length;

  // Installer sees only two columns (RRP + their own tier). Staff sees all four.
  // Installer view is entirely read-only — no Save, no Xero controls, no
  // auto-match — since the whole point of consolidating on this page is
  // that prices live in ONE place, edited only by staff.
  const installerTierKey = !isStaff && products[0]?.tier ? products[0].tier : null;
  const visibleTierColumns = isStaff
    ? TIER_COLUMNS
    : TIER_COLUMNS.filter(c => c.key === 'rrp' || c.key === installerTierKey);
  const pageTitle = isStaff ? 'Material & Quote Pricing' : 'Product Prices';
  const pageSubtitle = isStaff
    ? 'Retail rates installers charge their customers — RRP & per-tier prices. Installers only ever see RRP + their own tier price.'
    : 'Reference prices for your customer quotes. Prices are managed by Ox — for changes, contact us.';

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="text-white" style={{ background: 'linear-gradient(135deg,#0B0B0E 0%,#1f2937 75%)', borderBottom: '3px solid #7A0B7A' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(isStaff ? '/staff' : '/dashboard')} data-testid="pricing-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <PoundSterling className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#a78bfa' }} />
              {pageTitle}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">{pageSubtitle}</p>
          </div>
          {isStaff && hasPricelist && (
            <Button
              onClick={applyPricelist}
              variant="outline"
              size="sm"
              disabled={applyingPricelist}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
              data-testid="pricing-apply-pricelist-btn"
              aria-label="Apply printed pricelist to fill missing Trade prices"
              title="Fills any empty Trade price from the printed Ox pricelist and adds missing catalogue products. Hand-set prices are never overwritten."
            >
              <ListPlus className={`w-4 h-4 sm:mr-1.5 ${applyingPricelist ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{applyingPricelist ? 'Applying…' : 'Apply printed pricelist'}</span>
            </Button>
          )}
          {isStaff && xeroCandidates.length > 0 && (
            <Button
              onClick={copyXeroPricesBulk}
              variant="outline"
              size="sm"
              disabled={usingXero || xeroBlankTrade.length === 0}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
              data-testid="pricing-use-xero-prices-btn"
              title={xeroBlankTrade.length ? `Copy the Xero sales price into the empty Trade cell of ${xeroBlankTrade.length} linked product${xeroBlankTrade.length === 1 ? '' : 's'}. Typed prices are never overwritten.` : 'Every linked product already has a Trade price — use the per-row button to overwrite one.'}
            >
              <Download className={`w-4 h-4 sm:mr-1.5 ${usingXero ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{usingXero ? 'Copying…' : `Use Xero prices${xeroBlankTrade.length ? ` (${xeroBlankTrade.length})` : ''}`}</span>
            </Button>
          )}
          {isStaff && (
            <Button
              onClick={autoMatchXero}
              variant="outline"
              size="sm"
              disabled={autoMatching}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
              data-testid="pricing-xero-automatch-btn"
              aria-label="Auto-match every product SKU to its Xero item"
              title="Walks every unlinked product and links to the Xero item with the matching SKU"
            >
              <Link2 className={`w-4 h-4 sm:mr-1.5 ${autoMatching ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{autoMatching ? 'Matching…' : 'Auto-match Xero'}</span>
            </Button>
          )}
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      {/* Brand tabs */}
      <div className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-2 flex gap-1.5 overflow-x-auto">
          {BRAND_TABS.map(b => {
            const active = activeBrand === b.key;
            return (
              <button
                key={b.key}
                onClick={() => setActiveBrand(b.key)}
                data-testid={`pricing-tab-${b.key}`}
                className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 transition-colors whitespace-nowrap ${active ? 'text-white' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}
                style={active ? { backgroundColor: b.color, borderColor: b.color, color: b.ink || '#fff' } : undefined}
              >
                {b.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        {isStaff && activeBrand === 'proepoxy' && <ProEpoxyPackEditor />}
        {isStaff && <TierManagerCard tiers={tiers} onChanged={(next) => { if (next) setTiers(next); else reloadTiers(); load(); }} />}
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : products.length === 0 ? (
          <Card className="p-10 text-center" data-testid={`pricing-empty-${activeBrand}`}>
            <p className="text-base font-bold text-gray-900">No products in this brand yet</p>
            <p className="text-sm text-gray-500 mt-1">Add products via Stock → Manage Categories first; their prices will then appear here.</p>
          </Card>
        ) : (
          <>
            {dirtyCount > 0 && (
              <div className="sticky top-12 z-10 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-900 font-semibold" data-testid="pricing-dirty-banner">
                You have {dirtyCount} row{dirtyCount === 1 ? '' : 's'} with unsaved changes — click the save icon on each row.
              </div>
            )}
            {isStaff && <XeroFollowCard brand={activeBrand} onApplied={load} changedCount={products.filter(p => p.xero_differs).length} />}
            {isStaff && <XeroLinkStatusCard reloadKey={checkKey} onChanged={load} onLoaded={onLinkStatus} />}
            {isStaff && activeBrand === 'proepoxy' && <ProEpoxyItemLinks onChanged={() => { load(); setCheckKey(k => k + 1); }} />}
            {isStaff && activeBrand === 'probound' && (
              <>
                <QuoteToolPriceCheck brand="probound" reloadKey={checkKey} onHighlight={setHighlight} onRenamed={() => { load(); setCheckKey(k => k + 1); }} />
                <CoverageSettingsCard brand="probound" />
              </>
            )}
            {isStaff && (
              <div className="flex flex-wrap items-center gap-2" data-testid="pricing-row-filters">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Show</span>
                {[['all', `All (${products.length})`], ['unused', `Not used anywhere (${filterCounts.unused})`], ['nocost', `No cost (${filterCounts.nocost})`], ['lowmargin', `Margin under 20% (${filterCounts.lowmargin})`]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setRowFilter(k)} data-testid={`pricing-filter-${k}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${rowFilter === k ? 'bg-[#0B0B0E] text-white border-transparent' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`}>{l}</button>
                ))}
                {rowFilter === 'unused' && <span className="text-xs text-gray-500 ml-1">Not on any quote tool, order builder, order or customer quote, and nothing in stock — safe to remove.</span>}
                {rowFilter !== 'all' && !usedLoaded && <span className="text-xs text-gray-400">Checking usage…</span>}
              </div>
            )}
            {byCategory.length === 0 && rowFilter !== 'all' && (
              <Card className="p-8 text-center text-gray-500" data-testid="pricing-filter-empty">Nothing matches this filter on the {BRAND_TABS.find(b => b.key === activeBrand)?.label || activeBrand} tab.</Card>
            )}
            {byCategory.map(({ category, items }) => (
              <Card key={category} className="overflow-hidden" data-testid={`pricing-cat-${category}`}>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900">{category}</h3>
                  <p className="text-[11px] text-gray-500">{items.length} product{items.length === 1 ? '' : 's'}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`pricing-table-${activeBrand}`}>
                    <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Product</th>
                        {visibleTierColumns.map(c => (
                          <th key={c.key} className="text-right px-3 py-2 font-semibold whitespace-nowrap" title={c.help}>
                            {isStaff ? c.label : (c.key === 'rrp' ? 'RRP (charge customer)' : 'Your Cost')}
                            {isStaff && c.discount != null && <span className="block text-[10px] font-bold normal-case tracking-normal text-emerald-700" data-testid={`pricing-col-rule-${c.key}`}>RRP − {c.discount}%</span>}
                          </th>
                        ))}
                        {isStaff && (
                          <th className="text-right px-3 py-2 font-semibold whitespace-nowrap" title="What the product costs Ox (ex VAT). Pulled from the Xero item's purchase price; type a value to override.">Cost (ex VAT)</th>
                        )}
                        {isStaff && (
                          <th className="text-right px-3 py-2 font-semibold whitespace-nowrap" title="Trade price minus cost, ex VAT">Margin</th>
                        )}
                        {isStaff && (
                          <th className="text-center px-2 py-2 font-semibold whitespace-nowrap" title="Link this product to its Xero item so it can appear on Xero invoices">Xero</th>
                        )}
                        {isStaff && <th className="w-16"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(p => {
                        const edited = edits[p.id];
                        const dirty = !!edited;
                        const saving = savingIds.has(p.id);
                        const values = { ...p.prices, ...(edited || {}) };
                        return (
                          <tr key={p.id} id={`pricing-row-${p.id}`} className={`border-t border-gray-100 ${dirty ? 'bg-amber-50/50' : highlight.missing.has(p.id) ? 'bg-amber-50 border-l-4 border-l-amber-400' : highlight.xero.has(p.id) ? 'border-l-4 border-l-sky-300' : ''}`} data-testid={`pricing-row-${p.id}`}>
                            <td className="px-3 py-2 text-gray-900 font-medium">
                              {isStaff ? <ProductNameEdit product={p} onRenamed={(f) => setProducts(prev => prev.map(x => x.id === p.id ? { ...x, ...f } : x))} /> : p.name}
                              {isStaff && <UsedInTags usedIn={usedIn[p.id]} />}
                              {highlight.missing.has(p.id) && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-700" data-testid={`pricing-row-missing-${p.id}`}>needs a price</span>}
                              {highlight.xero.has(p.id) && p.xero_price != null && <span className="ml-2 text-[10px] font-semibold text-sky-700" data-testid={`pricing-row-xero-${p.id}`}>using Xero £{Number(p.xero_price).toFixed(2)}</span>}
                              {isStaff && p.xero_differs && p.xero_price != null && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-rose-700" data-testid={`pricing-differs-${p.id}`}>Xero differs £{Number(p.xero_price).toFixed(2)} · <button type="button" onClick={() => pushXero(p)} className="underline hover:text-rose-900" data-testid={`pricing-push-mine-${p.id}`}>Push mine</button> / <button type="button" onClick={() => takeXero(p)} className="underline hover:text-rose-900" data-testid={`pricing-take-xero-${p.id}`}>Take Xero's</button></span>}
                              {isStaff && p.xero_item_id && !p.xero_differs && <button type="button" onClick={() => pushXero(p)} className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold text-gray-500 hover:text-emerald-700" title="Update this item in Xero with the app's name, code, Trade price and cost" data-testid={`pricing-push-${p.id}`}><UploadCloud className="w-3 h-3" /> Update in Xero</button>}
                              {isStaff && p.xero_item_id && (p.price_pinned
                                ? <button type="button" onClick={() => togglePin(p, false)} className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:underline" title="Pinned — auto-follow leaves this product alone. Click to unpin and follow Xero again." data-testid={`pricing-pin-${p.id}`}><Pin className="w-3 h-3" /> pinned</button>
                                : p.prices_from_xero_at
                                  ? <button type="button" onClick={() => togglePin(p, true)} className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold text-sky-700 hover:underline" title="Prices follow Xero automatically. Click to pin and stop that for this product." data-testid={`pricing-pin-${p.id}`}><PinOff className="w-3 h-3" /> follows Xero</button>
                                  : null)}
                            </td>
                            {visibleTierColumns.map(c => (
                              <td key={c.key} className="px-2 py-1">
                                {isStaff && c.discount != null && Number(values.rrp) > 0 ? (
                                  <div className="h-8 flex items-center justify-end px-2 text-right text-emerald-800 bg-emerald-50/60 rounded-md" title={`RRP − ${c.discount}% — change the % in "Installer price tiers" above`} data-testid={`pricing-cell-${p.id}-${c.key}`}>
                                    £{(Number(values.rrp || 0) * (1 - c.discount / 100)).toFixed(2)}
                                  </div>
                                ) : isStaff ? (
                                  <Input
                                    type="number" step="0.01" min="0"
                                    value={values[c.key] ?? ''}
                                    onChange={(e) => onCellChange(p.id, c.key, e.target.value)}
                                    data-testid={`pricing-cell-${p.id}-${c.key}`}
                                    className={`h-8 text-right ${c.strong ? 'font-bold' : ''}`}
                                  />
                                ) : (
                                  <div
                                    className={`h-8 flex items-center justify-end px-2 text-right ${c.strong ? 'font-bold text-gray-900' : 'text-gray-700'}`}
                                    data-testid={`pricing-cell-${p.id}-${c.key}`}
                                  >
                                    £{Number(values[c.key] || 0).toFixed(2)}
                                  </div>
                                )}
                              </td>
                            ))}
                            {isStaff && (
                              <td className="px-2 py-1">
                                <CostCell product={p} onSaved={(row) => setProducts(ps => ps.map(x => x.id === row.id ? row : x))} />
                              </td>
                            )}
                            {isStaff && (
                              <td className="px-2 py-1">
                                <MarginCell product={p} tradePrice={values.trade} />
                              </td>
                            )}
                            {isStaff && (
                              <td className="px-2 py-1 text-center whitespace-nowrap">
                                <LinkToXeroButton product={p} onChanged={load} />
                                {rowFilter === 'unused' && isUnused(p) && (
                                  <button type="button" onClick={() => deleteProduct(p)} className="ml-1 p-1 text-gray-400 hover:text-rose-600 align-middle" title="Remove from catalogue" aria-label="Remove product" data-testid={`pricing-delete-${p.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                                )}
                                {p.xero_price != null && Number(p.xero_price) > 0 && Math.abs(Number(values.trade || 0) - Number(p.xero_price)) >= 0.005 && (
                                  <button
                                    type="button"
                                    onClick={() => copyXeroPriceRow(p)}
                                    disabled={saving}
                                    className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:text-sky-900 border border-sky-200 hover:border-sky-400 rounded px-1.5 py-0.5 bg-sky-50"
                                    title={`Copy Xero price £${Number(p.xero_price).toFixed(2)} into Trade`}
                                    data-testid={`pricing-use-xero-${p.id}`}
                                  >
                                    <Download className="w-3 h-3" /> Use £{Number(p.xero_price).toFixed(2)}
                                  </button>
                                )}
                              </td>
                            )}
                            {isStaff && (
                              <td className="px-2 py-1 text-center">
                                <Button
                                  size="sm" variant="ghost"
                                  disabled={!dirty || saving}
                                  onClick={() => saveRow(p)}
                                  data-testid={`pricing-save-${p.id}`}
                                  title={dirty ? 'Save changes' : 'No changes to save'}
                                  className={dirty ? 'text-emerald-700 hover:bg-emerald-50' : 'text-gray-300'}
                                >
                                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
